/**
 * Background Sync Manager
 * Coordinates background/foreground transitions across components
 * Helps ensure data is synced when app returns from background state
 */

class BackgroundSyncManager {
  constructor() {
    this._isVisible = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
    this._lastVisibleTime = Date.now();
    this._lastHiddenTime = null;
    this._syncCallbacks = new Map(); // componentId -> { callback, priority }
    this._pendingSyncs = new Set();
    this._syncInProgress = false;
    this._syncDebounceTimer = null;
    
    // Statistics for monitoring
    this._stats = {
      totalSyncs: 0,
      backgroundDurations: [],
      lastSyncTime: null,
      missedSyncs: 0
    };
    
    this._initializeListeners();
  }
  
  _initializeListeners() {
    if (typeof document === 'undefined') return;
    
    // Visibility change handler
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this._isVisible = false;
        this._lastHiddenTime = Date.now();
        console.log('[BackgroundSyncManager] 📱 App going to background');
      } else if (document.visibilityState === 'visible') {
        const timeInBackground = this._lastHiddenTime ? Date.now() - this._lastHiddenTime : 0;
        this._isVisible = true;
        this._lastVisibleTime = Date.now();
        
        // Track statistics
        if (timeInBackground > 0) {
          this._stats.backgroundDurations.push(timeInBackground);
          // Keep only last 10 durations
          if (this._stats.backgroundDurations.length > 10) {
            this._stats.backgroundDurations.shift();
          }
        }
        
        console.log(`[BackgroundSyncManager] 👁️ App returned to foreground (background for ${Math.round(timeInBackground/1000)}s)`);
        
        // Trigger sync callbacks based on background duration
        this._triggerSyncCallbacks(timeInBackground);
        
        this._lastHiddenTime = null;
      }
    });
    
    // Focus event for PWA support
    window.addEventListener('focus', () => {
      const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                    window.navigator.standalone === true;
      
      if (isPWA && this._lastHiddenTime) {
        console.log('[BackgroundSyncManager] 📱 PWA focus detected');
        // Trigger visibility change logic for PWA
        const timeInBackground = Date.now() - this._lastHiddenTime;
        if (timeInBackground > 5000) { // Only if >5s in background
          this._triggerSyncCallbacks(timeInBackground);
        }
      }
    });
    
    // Online event - trigger sync when network comes back
    window.addEventListener('online', () => {
      console.log('[BackgroundSyncManager] 🌐 Network online');
      setTimeout(() => {
        this._triggerSyncCallbacks(0, { reason: 'network_restored' });
      }, 1000);
    });
  }
  
  /**
   * Register a sync callback for a component
   * @param {string} componentId - Unique identifier for the component
   * @param {Function} callback - Function to call on sync. Receives { timeInBackground, reason }
   * @param {Object} options - { priority: number (lower = first), minBackgroundTime: ms }
   * @returns {Function} Unsubscribe function
   */
  registerSyncCallback(componentId, callback, options = {}) {
    const { priority = 10, minBackgroundTime = 0 } = options;
    
    this._syncCallbacks.set(componentId, {
      callback,
      priority,
      minBackgroundTime
    });
    
    console.log(`[BackgroundSyncManager] Registered sync callback: ${componentId} (priority: ${priority})`);
    
    // Return unsubscribe function
    return () => {
      this._syncCallbacks.delete(componentId);
      console.log(`[BackgroundSyncManager] Unregistered sync callback: ${componentId}`);
    };
  }
  
  /**
   * Trigger all sync callbacks
   * @param {number} timeInBackground - Time spent in background (ms)
   * @param {Object} options - Additional options like { reason: string }
   */
  _triggerSyncCallbacks(timeInBackground, options = {}) {
    if (this._syncInProgress) {
      console.log('[BackgroundSyncManager] Sync already in progress, queuing...');
      this._pendingSyncs.add({ timeInBackground, ...options });
      return;
    }
    
    if (this._syncDebounceTimer) {
      clearTimeout(this._syncDebounceTimer);
    }
    
    // Debounce rapid visibility changes
    this._syncDebounceTimer = setTimeout(() => {
      this._executeSyncCallbacks(timeInBackground, options);
    }, 100);
  }
  
  _executeSyncCallbacks(timeInBackground, options) {
    this._syncInProgress = true;
    const reason = options.reason || 'visibility_change';
    
    // Sort callbacks by priority
    const sortedCallbacks = Array.from(this._syncCallbacks.entries())
      .filter(([_, config]) => timeInBackground >= config.minBackgroundTime)
      .sort((a, b) => a[1].priority - b[1].priority);
    
    console.log(`[BackgroundSyncManager] Executing ${sortedCallbacks.length} sync callbacks (reason: ${reason})`);
    
    let successCount = 0;
    let errorCount = 0;
    
    sortedCallbacks.forEach(([componentId, config]) => {
      try {
        config.callback({
          timeInBackground,
          reason,
          isLongBackground: timeInBackground > 30000,
          isVeryLongBackground: timeInBackground > 120000
        });
        successCount++;
      } catch (error) {
        console.error(`[BackgroundSyncManager] Error in sync callback for ${componentId}:`, error);
        errorCount++;
      }
    });
    
    // Update statistics
    this._stats.totalSyncs++;
    this._stats.lastSyncTime = Date.now();
    this._stats.missedSyncs += errorCount;
    
    console.log(`[BackgroundSyncManager] Sync complete: ${successCount} success, ${errorCount} errors`);
    
    this._syncInProgress = false;
    
    // Process any pending syncs
    if (this._pendingSyncs.size > 0) {
      const nextSync = Array.from(this._pendingSyncs)[0];
      this._pendingSyncs.delete(nextSync);
      this._triggerSyncCallbacks(nextSync.timeInBackground, nextSync);
    }
  }
  
  /**
   * Manually trigger a sync (e.g., after push notification)
   * @param {Object} options - { reason: string, force: boolean }
   */
  triggerManualSync(options = {}) {
    const { reason = 'manual', force = false } = options;
    console.log(`[BackgroundSyncManager] Manual sync triggered (reason: ${reason})`);
    
    if (force) {
      this._executeSyncCallbacks(0, { reason });
    } else {
      this._triggerSyncCallbacks(0, { reason });
    }
  }
  
  /**
   * Check if app is currently visible
   */
  isVisible() {
    return this._isVisible;
  }
  
  /**
   * Get time since last visibility
   */
  getTimeSinceVisible() {
    return this._isVisible ? 0 : (Date.now() - (this._lastHiddenTime || Date.now()));
  }
  
  /**
   * Get sync statistics
   */
  getStats() {
    return {
      ...this._stats,
      isVisible: this._isVisible,
      registeredCallbacks: this._syncCallbacks.size,
      avgBackgroundDuration: this._stats.backgroundDurations.length > 0
        ? Math.round(this._stats.backgroundDurations.reduce((a, b) => a + b, 0) / this._stats.backgroundDurations.length)
        : 0
    };
  }
}

// Create singleton instance
const backgroundSyncManager = new BackgroundSyncManager();

export default backgroundSyncManager;
